import {
  Button,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Typography,
  message,
} from "antd";
import React from "react";
import { useFetcher } from "react-router-dom";
import { useFetcherBusy } from "~/hooks/useRouteActivity";
import { CustomTextField } from "~/components";
import { AccessControlledButton } from "~/components/Buttons/AccessControlledButton";
import { removeEmptyFields, INTENTS, dateToLocalTime } from "~/utils";
import { useTranslation } from "react-i18next";
import * as APITypes from "~/API";

export const OrganizationDetailsTab = ({
  organizationDetails,
  canEditOrganization,
  canDeleteOrganization,
}: {
  organizationDetails: APITypes.Organization;
  canEditOrganization: boolean;
  canDeleteOrganization: boolean;
}) => {
  const { t } = useTranslation();
  const [formInstance] = Form.useForm();
  const fetcher = useFetcher();
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [showEditConfirm, setShowEditConfirm] = React.useState(false);
  const isSubmitting = useFetcherBusy(fetcher);

  const initialFormValues = {
    name: organizationDetails.name || "",
  };

  const toggleEditForm = () => setShowEditForm(!showEditForm);
  const toggleEditConfirm = () => setShowEditConfirm(!showEditConfirm);

  const handleEditSubmit = (formValues: any) => {
    const editFormValues = removeEmptyFields(formValues);

    fetcher.submit(
      {
        body: JSON.stringify({
          ...editFormValues,
          _version: organizationDetails._version,
        }),
        intent: INTENTS._edit_organization,
      },
      { method: "post" }
    );
    toggleEditForm();
  };

  const handleDeleteOrganization = () => {
    message.info("Delete organization: To be implemented");
    console.log("Delete organization: To be implemented");
  };

  return (
    <div data-tour="org-details-tab-content">
      <Space
        style={{
          marginBottom: "1rem",
          marginTop: "1rem",
        }}
        data-tour="org-action-buttons"
      >
        {showEditForm ? (
          <>
            <Popconfirm
              title={t("Confirm_changes")}
              onCancel={toggleEditConfirm}
              open={showEditConfirm}
              onConfirm={() => {
                formInstance.submit();
                toggleEditConfirm();
              }}
            >
              <Button
                type="primary"
                ghost
                onClick={() => toggleEditConfirm()}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {t("Submit")}
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              ghost
              danger
              onClick={() => toggleEditForm()}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </Button>
          </>
        ) : (
          <AccessControlledButton
            canPerform={canEditOrganization}
            label={t("Edit")}
            tooltipTitle={t("Only_SuperAdmin_Can_Edit_Organization")}
            submitting={isSubmitting}
            buttonProps={{
              type: "primary",
              ghost: true,
              disabled: showEditForm,
            }}
            onClick={() => toggleEditForm()}
          />
        )}

        <AccessControlledButton
          canPerform={canDeleteOrganization}
          label={t("Delete")}
          tooltipTitle={t("Only_SuperAdmin_Can_Delete_Organization")}
          submitting={isSubmitting}
          buttonProps={{
            type: "primary",
            danger: true,
            disabled: showEditForm,
          }}
          confirm={{
            title: t("Delete_Organization_Warning"),
            okText: t("Delete"),
            okButtonProps: { danger: true },
            onConfirm: handleDeleteOrganization,
          }}
        />

      </Space>

      <div style={{ marginTop: "2rem" }} data-tour="org-info">
        {showEditForm ? (
          <Form
            initialValues={initialFormValues}
            layout="vertical"
            onFinish={handleEditSubmit}
            form={formInstance}
            disabled={isSubmitting}
          >
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={t("Name")}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: t("Enter_a_name"),
                    },
                  ]}
                >
                  <Input placeholder={t("Enter_a_name")} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        ) : (
          <>
            <Row gutter={12}>
              <Col span={8}>
                <Space direction="vertical">
                  <Typography.Text strong style={{ marginBottom: 5 }}>
                    {t("Name")}
                  </Typography.Text>
                  <CustomTextField value={organizationDetails.name || ""} />
                </Space>
              </Col>
              <Col span={8}>
                <Space direction="vertical">
                  <Typography.Text strong style={{ marginBottom: 5 }}>
                    {t("Created_At")}
                  </Typography.Text>
                  <CustomTextField
                    value={
                      organizationDetails.createdAt
                        ? dateToLocalTime(organizationDetails.createdAt, "date")
                        : "n/a"
                    }
                  />
                </Space>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};
